import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

// Extend Window interface for Tawk_API
declare global {
  interface Window {
    Tawk_API?: {
      onLoad?: () => void;
      onStatusChange?: (status: string) => void;
      hideWidget?: () => void;
      showWidget?: () => void;
      toggle?: () => void;
      maximize?: () => void;
      minimize?: () => void;
      setAttributes?: (attributes: Record<string, any>) => void;
    };
    Tawk_LoadStart?: Date;
  }
}

interface TawkToConfig {
  propertyId: string;
  widgetId: string;
  enabled?: boolean;
  customAttributes?: Record<string, any>;
  onLoad?: () => void;
  onStatusChange?: (status: string) => void;
}

/**
 * Custom hook for tawk.to integration
 * Only loads for authenticated users and provides configuration options
 */
export const useTawkTo = (config: TawkToConfig) => {
  const { isAuthenticated, user } = useAuth();
  const scriptLoaded = useRef(false);
  const { 
    propertyId, 
    widgetId, 
    enabled = true, 
    customAttributes = {},
    onLoad,
    onStatusChange 
  } = config;

  useEffect(() => {
    // Only load for authenticated users when enabled
    if (!isAuthenticated || !enabled || scriptLoaded.current) {
      return;
    }

    // Prevent multiple script loads
    scriptLoaded.current = true;

    // Set load start time
    window.Tawk_LoadStart = new Date();

    // Configure Tawk_API before script loads
    window.Tawk_API = window.Tawk_API || {};

    // Set user attributes if available
    if (user) {
      const attributes = {
        name: user.name || user.email,
        email: user.email,
        userId: user.id,
        ...customAttributes
      };

      window.Tawk_API.onLoad = () => {
        window.Tawk_API?.setAttributes?.(attributes);
        // Hide widget by default to prevent auto-popup
        window.Tawk_API?.hideWidget?.();
        onLoad?.();
      };
    } else if (onLoad) {
      window.Tawk_API.onLoad = () => {
        // Hide widget by default to prevent auto-popup
        window.Tawk_API?.hideWidget?.();
        onLoad?.();
      };
    } else {
      window.Tawk_API.onLoad = () => {
        // Hide widget by default to prevent auto-popup
        window.Tawk_API?.hideWidget?.();
      };
    }

    // Set status change handler
    if (onStatusChange) {
      window.Tawk_API.onStatusChange = onStatusChange;
    }

    // Create and inject script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://embed.tawk.to/${propertyId}/${widgetId}`;
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    
    // Handle script load errors
    script.onerror = () => {
      console.error('Failed to load tawk.to chat widget');
      scriptLoaded.current = false;
    };

    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Note: We don't remove the script on cleanup as tawk.to handles its own lifecycle
      // But we can hide the widget if needed
      if (window.Tawk_API?.hideWidget) {
        window.Tawk_API.hideWidget();
      }
    };
  }, [isAuthenticated, enabled, propertyId, widgetId, user, customAttributes, onLoad, onStatusChange]);

  // Return control functions
  return {
    show: () => window.Tawk_API?.showWidget?.(),
    hide: () => window.Tawk_API?.hideWidget?.(),
    toggle: () => window.Tawk_API?.toggle?.(),
    maximize: () => window.Tawk_API?.maximize?.(),
    minimize: () => window.Tawk_API?.minimize?.(),
    isLoaded: () => !!window.Tawk_API,
  };
}; 